import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { IconCheck, IconArrowLeft, IconArrowRight, IconLock, IconShoppingBag } from '@tabler/icons-react';
import { useCart } from '../context/CartContext';
import { useSiteContent } from '../context/SiteContentContext';
import { useShippingZones, checkPromoCode, createCheckout, CHECKOUT_FORM_KEY } from '../lib/storeData';
import { useProducts } from '../context/ProductsContext';
import { getCountryList, getZoneForCountry } from '../lib/countries';
import './CheckoutPage.css';

var steps = ['Cart', 'Details', 'Shipping', 'Payment', 'Confirm'];
var countryList = getCountryList();

// Restores what the customer typed if they come back from Stripe
function loadSavedForm() {
  try {
    return JSON.parse(sessionStorage.getItem(CHECKOUT_FORM_KEY) || '{}');
  } catch (e) {
    return {};
  }
}

export default function CheckoutPage() {

  var cart = useCart();
  var items = cart.items;
  var subtotal = cart.subtotal;
  var syncWithProducts = cart.syncWithProducts;
  var productsCtx = useProducts();
  var liveProducts = productsCtx.products;
  var productsLoading = productsCtx.loading;
  var productsError = productsCtx.loadError;
  var content = useSiteContent();
  var c = content.c;
  var zones = useShippingZones().zones;

  var [searchParams] = useSearchParams();
  var [saved] = useState(loadSavedForm);
  var cameBackFromStripe = searchParams.get('cancelled') === '1';

  var _step = useState(function() { return cameBackFromStripe && saved.details ? 4 : 2; });
  var step = _step[0]; var setStep = _step[1];
  var _details = useState(function() { return saved.details || { firstName: '', lastName: '', email: '', phone: '' }; });
  var details = _details[0]; var setDetails = _details[1];
  var _address = useState(function() { return saved.address || { street: '', apt: '', city: '', state: '', zip: '', country: 'Guyana' }; });
  var address = _address[0]; var setAddress = _address[1];
  var _shipping = useState(function() { return saved.shipping || { method: 'standard' }; });
  var shipping = _shipping[0]; var setShipping = _shipping[1];
  var [reminderConsent, setReminderConsent] = useState(saved.reminderConsent === true);
  var [paying, setPaying] = useState(false);
  var [payError, setPayError] = useState('');
  var [cartNotice, setCartNotice] = useState('');
  var _promoCode = useState(saved.promoCode || '');
  var promoCode = _promoCode[0]; var setPromoCode = _promoCode[1];
  var _promoResult = useState(saved.promoResult || null);
  var promoResult = _promoResult[0]; var setPromoResult = _promoResult[1];
  var [promoChecking, setPromoChecking] = useState(false);
  var [promoNote, setPromoNote] = useState('');
  var orderNumber = ''; // old step 5 only; the thank-you page now shows the real number

  // Refresh the bag against current products once they've loaded properly
  var syncedRef = useRef(false);
  useEffect(function() {
    if (syncedRef.current || productsLoading || productsError) return;
    syncedRef.current = true;
    var result = syncWithProducts(liveProducts);
    if (result.removed > 0 || result.changed > 0) {
      setCartNotice('Your bag was updated with the latest prices and availability. Please check your order summary.');
    }
  }, [productsLoading, productsError, liveProducts, syncWithProducts]);

  // If the customer presses Back from Stripe, make sure the Pay button works again
  useEffect(function() {
    function onPageShow(e) { if (e.persisted) setPaying(false); }
    window.addEventListener('pageshow', onPageShow);
    return function() { window.removeEventListener('pageshow', onPageShow); };
  }, []);
  var _errors = useState({});
  var errors = _errors[0]; var setErrors = _errors[1];

  var zoneName = getZoneForCountry(address.country);
  var matchedZone = zones.find(function(z) { return z.name === zoneName; }) || zones[zones.length - 1];

  var shippingCost = matchedZone
    ? (shipping.method === 'express' ? matchedZone.express_rate : matchedZone.standard_rate)
    : 0;

  var discount = promoResult ? (subtotal * promoResult.discount_percent / 100) : 0;
  var freeThreshold = Number(c('free_shipping_threshold', '500'));
  var finalShipping = subtotal >= freeThreshold ? 0 : shippingCost;
  var total = subtotal - discount + finalShipping;

  var storeStatus = c('store_status', 'open');

  async function applyPromo() {
    var code = promoCode.trim();
    if (!code || promoChecking) return;
    setPromoChecking(true);
    var res = await checkPromoCode(code);
    setPromoChecking(false);
    if (res.error) {
      setPromoResult(null);
      setPromoNote(res.error);
      return;
    }
    setPromoNote('');
    // false = checked and not valid, so the "Invalid code" message shows
    setPromoResult(res.promo || false);
  }

  function validateStep2() {
    var e = {};
    if (!details.firstName.trim()) e.firstName = 'First name is required';
    if (!details.lastName.trim()) e.lastName = 'Last name is required';
    if (!details.email.trim()) e.email = 'Email is required';
    else if (!details.email.includes('@')) e.email = 'Enter a valid email';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function validateStep3() {
    var e = {};
    if (!address.street.trim()) e.street = 'Street address is required';
    if (!address.city.trim()) e.city = 'City is required';
    if (!address.country.trim()) e.country = 'Country is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

    function goStep(n) {
    if (n === 3 && !validateStep2()) return;
    if (n === 4 && !validateStep3()) return;
    setErrors({});
    setStep(n);
  }

  async function startPayment() {
    if (paying) return;
    setPayError('');
    setPaying(true);

    // Remember the form in this tab in case they cancel and come back
    try {
      sessionStorage.setItem(CHECKOUT_FORM_KEY, JSON.stringify({
        details: details,
        address: address,
        shipping: shipping,
        promoCode: promoCode,
        promoResult: promoResult,
        reminderConsent: reminderConsent
      }));
    } catch (e) {
      // Storage blocked or full: carry on, they'd just retype on cancel
    }

    // Only choices go to the server. Prices are worked out there.
    var res = await createCheckout({
      items: items.map(function(item) {
        return { productId: item.productId, variantId: item.variantId, colourId: item.colourId, quantity: item.quantity };
      }),
      customer: details,
      address: address,
      shippingMethod: shipping.method,
      promoCode: promoResult ? promoResult.code : '',
      reminderConsent: reminderConsent
    });

    if (res.error) {
      setPaying(false);
      setPayError(res.error);
      return;
    }
    window.location.href = res.url;
  }

  function fieldError(name) {
    if (!errors[name]) return null;
    return <div style={{ fontSize: 11, color: '#8B2020', marginTop: 3 }}>{errors[name]}</div>;
  }

  function fieldClass(name) {
    return errors[name] ? 'co-input co-input--error' : 'co-input';
  }

  if ((storeStatus === 'paused' || storeStatus === 'closed') && step < 5) {
    return (
      <div className="co-page">
        <nav className="co-nav"><Link to="/" className="co-logo">Andre <span>M</span> Leather</Link></nav>
        <div className="co-empty">
          <IconShoppingBag size={32} />
          <h2>Orders are currently paused</h2>
          <p>{c('store_pause_message', 'Ordering is temporarily paused.')}</p>
          <Link to="/shop" className="co-empty-btn">Browse the collection</Link>
        </div>
      </div>
    );
  }

  if (items.length === 0 && step < 5) {
    return (
      <div className="co-page">
        <nav className="co-nav"><Link to="/" className="co-logo">Andre <span>M</span> Leather</Link></nav>
        <div className="co-empty">
          <IconShoppingBag size={32} />
          <h2>Your bag is empty</h2>
          <p>Add some items before checking out.</p>
          <Link to="/shop" className="co-empty-btn">Browse the collection</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="co-page">
      <nav className="co-nav"><Link to="/" className="co-logo">Andre <span>M</span> Leather</Link></nav>

      <div className="co-steps">
        {steps.map(function(label, i) {
          var num = i + 1;
          var isDone = num < step;
          var isActive = num === step;
          return (
            <div key={i} className="co-step-row">
              {i > 0 && <div className="co-step-line" />}
              <div className={'co-step-item' + (isDone ? ' co-step--done' : '') + (isActive ? ' co-step--active' : '')}>
                <div className="co-step-num">
                  {isDone ? <IconCheck size={10} /> : num}
                </div>
                <span>{label}</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="container co-body">
        <div className="co-main">

          {step === 2 && (
            <div>
              <h2 className="co-section-title">Your details</h2>
              <div className="co-divider">Enter your details</div>
              <div className="co-row2">
                <div className="co-field"><label>First name *</label><input className={fieldClass('firstName')} value={details.firstName} onChange={function(e) { setDetails(Object.assign({}, details, { firstName: e.target.value })); }} />{fieldError('firstName')}</div>
                <div className="co-field"><label>Last name *</label><input className={fieldClass('lastName')} value={details.lastName} onChange={function(e) { setDetails(Object.assign({}, details, { lastName: e.target.value })); }} />{fieldError('lastName')}</div>
              </div>
              <div className="co-field"><label>Email address *</label><input className={fieldClass('email')} type="email" value={details.email} onChange={function(e) { setDetails(Object.assign({}, details, { email: e.target.value })); }} />{fieldError('email')}</div>
              <div className="co-field"><label>Phone (optional)</label><input className="co-input" value={details.phone} onChange={function(e) { setDetails(Object.assign({}, details, { phone: e.target.value })); }} /></div>
              <div className="co-btns">
                <Link to="/shop" className="co-back"><IconArrowLeft size={13} /> Back to shop</Link>
                <button className="co-next" onClick={function() { goStep(3); }}>Continue to shipping <IconArrowRight size={13} /></button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h2 className="co-section-title">Shipping address</h2>
              <div className="co-field"><label>Street address *</label><input className={fieldClass('street')} value={address.street} onChange={function(e) { setAddress(Object.assign({}, address, { street: e.target.value })); }} />{fieldError('street')}</div>
              <div className="co-field"><label>Apartment, suite (optional)</label><input className="co-input" value={address.apt} onChange={function(e) { setAddress(Object.assign({}, address, { apt: e.target.value })); }} /></div>
              <div className="co-row2">
                <div className="co-field"><label>City *</label><input className={fieldClass('city')} value={address.city} onChange={function(e) { setAddress(Object.assign({}, address, { city: e.target.value })); }} />{fieldError('city')}</div>
                <div className="co-field"><label>State / Province</label><input className="co-input" value={address.state} onChange={function(e) { setAddress(Object.assign({}, address, { state: e.target.value })); }} /></div>
              </div>
              <div className="co-row2">
                <div className="co-field"><label>ZIP / Postal code</label><input className="co-input" value={address.zip} onChange={function(e) { setAddress(Object.assign({}, address, { zip: e.target.value })); }} /></div>
                <div className="co-field"><label>Country *</label>
                  <select className={fieldClass('country')} value={address.country} onChange={function(e) { setAddress(Object.assign({}, address, { country: e.target.value })); }}>
                    {countryList.map(function(name) { return <option key={name} value={name}>{name}</option>; })}
                  </select>
                  {fieldError('country')}
                </div>
              </div>

              {matchedZone && (
                <div className="co-shipping-options">
                  <h3>Shipping method</h3>
                  <label className={'co-ship-opt' + (shipping.method === 'standard' ? ' co-ship-opt--active' : '')}>
                    <input type="radio" name="ship" checked={shipping.method === 'standard'} onChange={function() { setShipping({ method: 'standard' }); }} />
                    <div>
                      <div className="co-ship-name">{'Standard \u2014 $' + matchedZone.standard_rate}</div>
                      <div className="co-ship-est">{matchedZone.standard_estimate}</div>
                    </div>
                  </label>
                  <label className={'co-ship-opt' + (shipping.method === 'express' ? ' co-ship-opt--active' : '')}>
                    <input type="radio" name="ship" checked={shipping.method === 'express'} onChange={function() { setShipping({ method: 'express' }); }} />
                    <div>
                      <div className="co-ship-name">{'Express \u2014 $' + matchedZone.express_rate}</div>
                      <div className="co-ship-est">{matchedZone.express_estimate}</div>
                    </div>
                  </label>
                  {subtotal >= freeThreshold && <div className="co-free-ship">{'\u2713 Free shipping on orders over $' + freeThreshold}</div>}
                </div>
              )}

              <div className="co-lead-callout">{'Made to order \u2014 ' + c('lead_time_standard', '2\u20133 week') + ' lead time'}</div>

              <div className="co-btns">
                <button className="co-back" onClick={function() { setStep(2); }}><IconArrowLeft size={13} /> Back</button>
                <button className="co-next" onClick={function() { goStep(4); }}>Continue to payment <IconArrowRight size={13} /></button>
              </div>
            </div>
          )}

                    {step === 4 && (
            <div>
              <h2 className="co-section-title">Review and pay</h2>

              {cameBackFromStripe && (
                <div className="co-notice">{'Payment cancelled. Nothing was charged. Your bag and details are still here.'}</div>
              )}

              <div className="co-review">
                <div className="co-review-row">
                  <div className="co-review-label">Ship to</div>
                  <div>
                    {details.firstName + ' ' + details.lastName}<br />
                    {address.street + (address.apt ? ', ' + address.apt : '')}<br />
                    {address.city + (address.state ? ', ' + address.state : '') + (address.zip ? ' ' + address.zip : '')}<br />
                    {address.country}
                  </div>
                  <button className="co-review-change" onClick={function() { setStep(3); }}>Change</button>
                </div>
                <div className="co-review-row">
                  <div className="co-review-label">Shipping</div>
                  <div>
                    {(shipping.method === 'express' ? 'Express' : 'Standard') +
                      (matchedZone ? ' \u2014 ' + (shipping.method === 'express' ? matchedZone.express_estimate : matchedZone.standard_estimate) : '')}
                  </div>
                  <button className="co-review-change" onClick={function() { setStep(3); }}>Change</button>
                </div>
                <div className="co-review-row">
                  <div className="co-review-label">Confirmation</div>
                  <div>{'Your confirmation will go to '}<strong>{details.email}</strong></div>
                  <button className="co-review-change" onClick={function() { setStep(2); }}>Change</button>
                </div>
              </div>

              <label className="co-consent">
                <input type="checkbox" checked={reminderConsent} onChange={function(e) { setReminderConsent(e.target.checked); }} />
                <span>{"Email me a reminder if I don't finish my order"}</span>
              </label>

              {payError && <div className="co-pay-error">{payError}</div>}

              <div className="co-secure"><IconLock size={12} /> {"You'll pay on Stripe's secure page. Andre M Leather never sees your card details."}</div>

              <div className="co-btns">
                <button className="co-back" disabled={paying} onClick={function() { setStep(3); }}><IconArrowLeft size={13} /> Back</button>
                <button className="co-place" disabled={paying} onClick={startPayment} style={{ opacity: paying ? 0.7 : 1 }}>
                  <IconLock size={13} /> {paying ? 'Taking you to secure payment...' : 'Pay securely \u2014 $' + total.toFixed(2)}
                </button>
              </div>
              <p className="co-final-note">{'The final total is confirmed on the payment page.'}</p>
            </div>
          )}

          {step === 5 && (
            <div className="co-confirm">
              <div className="co-confirm-icon"><IconCheck size={28} /></div>
              <h2 className="co-confirm-title">Order confirmed!</h2>
              <p className="co-confirm-sub">
                {'Thank you \u2014 your order is in. A confirmation email is on its way to ' + (details.email || 'you') + '. Andre will begin crafting your bag shortly.'}
              </p>
              <div className="co-confirm-details">
                <div className="co-confirm-row"><span>Order number</span><strong>{orderNumber}</strong></div>
                <div className="co-confirm-row"><span>Estimated lead time</span><strong>{c('lead_time_standard', '2\u20133 weeks')}</strong></div>
                <div className="co-confirm-row"><span>Total charged</span><strong>{'$' + total.toFixed(2)}</strong></div>
              </div>
              <div className="co-confirm-next">
                <h3>What happens next</h3>
                <p>{'Confirmation email sent now. Crafting begins within 48 hours. You\u2019ll receive a shipping email with tracking when your bag is dispatched.'}</p>
              </div>
              <div className="co-confirm-btns">
                <Link to="/" className="co-next">Continue shopping</Link>
              </div>
            </div>
          )}

        </div>

        {step < 5 && (
          <aside className="co-sidebar">
            <h3 className="co-sidebar-title">Order summary</h3>
            {items.map(function(item) {
              var thumbProduct = productsCtx.getProductById(item.productId);
              var thumb = thumbProduct && thumbProduct.images ? thumbProduct.images[0] : null;
              return (
                <div className="co-sidebar-item" key={item.key}>
                  <div className="co-sidebar-img">
                    {thumb ? <img src={thumb} alt="" /> : <IconShoppingBag size={16} />}
                  </div>
                  <div className="co-sidebar-info">
                    <div className="co-sidebar-name">{item.name}</div>
                    <div className="co-sidebar-variant">{[item.variantName, item.colourName, 'Qty ' + item.quantity].filter(Boolean).join(' \u00b7 ')}</div>
                  </div>
                  <div className="co-sidebar-price">{'$' + (item.price * item.quantity).toFixed(2)}</div>
                </div>
              );
            })}
            {cartNotice && <div className="co-notice">{cartNotice}</div>}
            <div className="co-promo">
              <input placeholder="Promo code" value={promoCode} onChange={function(e) { setPromoCode(e.target.value); setPromoResult(null); setPromoNote(''); }} />
              <button onClick={applyPromo} disabled={promoChecking}>{promoChecking ? 'Checking...' : 'Apply'}</button>
            </div>
            {promoNote && <div className="co-promo-msg co-promo--err">{promoNote}</div>}
            {promoResult !== null && (
              <div className={'co-promo-msg ' + (promoResult ? 'co-promo--ok' : 'co-promo--err')}>
                {promoResult ? '\u2713 Code applied \u2014 ' + promoResult.discount_percent + '% off' : 'Invalid code. Check spelling and try again.'}
              </div>
            )}
            <div className="co-totals">
              <div className="co-total-row"><span>Subtotal</span><span>{'$' + subtotal.toFixed(2)}</span></div>
              {discount > 0 && <div className="co-total-row co-total--discount"><span>Discount</span><span>{'-$' + discount.toFixed(2)}</span></div>}
              <div className="co-total-row"><span>Shipping</span><span>{step < 3 ? 'Calculated next' : (finalShipping === 0 ? 'Free' : '$' + finalShipping.toFixed(2))}</span></div>
              <div className="co-total-row"><span>Tax</span><span>$0.00</span></div>
              <div className="co-total-row co-total--grand"><span>Total</span><span>{'$' + total.toFixed(2)}</span></div>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}